import { useState } from 'react'
import { useTranslation } from '@/clientWeb/i18n/LanguageContext'
import { motion } from 'framer-motion'
import { ArrowLeft, Users, Clock, TrendingUp, Calendar, CheckCircle, Circle, Search } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { Badge } from '@/clientWeb/components/ui/badge'
import { EmptyState } from '@/clientWeb/components/ui/empty-state'
import { NoData } from '@/clientWeb/components/ui/no-data'
import nodataAnalyticsImg from '@/assets/nodataanalytics.svg'

interface StandupMeeting {
  id: string
  title: string
  date: string
  time: string
  duration: string
  participants: number
  status: string
  items: string[]
}

// No analytics API yet (see services/analytics.repo.ts), so there are no
// stand-ups to chart or list. These used to be an invented week and four
// made-up stand-ups with fixed headline numbers (17 this week, 94% on schedule).
const WEEKLY_DATA: { day: string; meetings: number; avgDuration: number }[] = []
const STANDUP_MEETINGS: StandupMeeting[] = []

export default function StandupMeetingsPage() {
  const t = useTranslation()
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [expanded, setExpanded] = useState<string | null>(null)

  const filtered = STANDUP_MEETINGS.filter(m =>
    m.title.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <div className="px-4 sm:px-6 py-3 shrink-0">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
          <button onClick={() => navigate('/analytics')} className="text-primary font-semibold hover:text-foreground flex items-center gap-1 transition-colors">
            <ArrowLeft className="w-3 h-3" />{t("analytics.title")}
          </button>
          <span>/</span><span className="text-foreground">{t("analytics.standupMeetings")}</span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 sm:px-6 sm:pb-4 sm:pt-2 space-y-4">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'This Week', value: STANDUP_MEETINGS.length, icon: Calendar, color: 'text-blue-500', bg: 'bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20' },
            { label: 'Avg Duration', value: '—', icon: Clock, color: 'text-amber-500', bg: 'bg-gradient-to-br from-amber-50 to-orange-100 dark:from-amber-900/20 dark:to-orange-900/20' },
            { label: t('analytics.avgParticipants'), value: '—', icon: Users, color: 'text-violet-500', bg: 'bg-gradient-to-br from-violet-50 to-purple-100 dark:from-violet-900/20 dark:to-purple-900/20' },
            { label: 'On Schedule', value: '—', icon: TrendingUp, color: 'text-green-500', bg: 'bg-gradient-to-br from-emerald-50 to-teal-100 dark:from-emerald-900/20 dark:to-teal-900/20' },
          ].map(({ label, value, icon: Icon, color, bg }, i) => (
            <motion.div key={label} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="relative bg-card dark:bg-card border border-border rounded-2xl p-4 shadow-xs hover:shadow-md transition-all duration-200 overflow-hidden">
              {/* Decorative Background Wave */}
              <div className={`absolute inset-0 ${bg}`}>
                <svg 
                  className="absolute bottom-0 right-0 w-full h-full" 
                  viewBox="0 0 200 120" 
                  fill="none" 
                  preserveAspectRatio="none"
                >
                  <path 
                    d="M200 120V80C180 70 160 75 140 80C120 85 100 90 80 85C60 80 40 70 20 75C10 77.5 5 79 0 80V120H200Z" 
                    fill="currentColor" 
                    className="text-white/30 dark:text-white/10"
                  />
                </svg>
              </div>

              {/* Content */}
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-8 h-8 rounded-lg bg-card/50 dark:bg-card/10 flex items-center justify-center">
                    <Icon className={`w-4 h-4 ${color}`} />
                  </div>
                </div>
                <div className="text-xl font-bold text-foreground mb-1">{value}</div>
                <div className="text-sm font-medium text-muted-foreground">{label}</div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Chart */}
        <div className="bg-card border border-border rounded-2xl p-4">
          <h3 className="text-sm font-semibold text-foreground mb-4">Weekly Standup Frequency</h3>
          {WEEKLY_DATA.length === 0 ? (
            <NoData image={nodataAnalyticsImg} title={t("analytics.noDataAvailable")} size="sm" showActionIcon={false} />
          ) : (
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={WEEKLY_DATA} barSize={36}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} />
              <YAxis tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} />
              <Tooltip contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, fontSize: 12 }} labelStyle={{ color: 'var(--foreground)' }} itemStyle={{ color: 'inherit' }} wrapperClassName="text-primary" />
              <Bar dataKey="meetings" fill="var(--primary)" radius={[6, 6, 0, 0]} name="Meetings" />
            </BarChart>
          </ResponsiveContainer>
          )}
        </div>

        {/* List */}
        <div>
          <div className="flex items-center gap-3 mb-3">
            <div className="flex-1 flex items-center gap-2 bg-muted/50 rounded-xl px-3 py-2">
              <Search className="w-4 h-4 text-muted-foreground" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t("analytics.searchStandupsPlaceholder")}
                className="flex-1 bg-transparent text-sm outline-none text-foreground placeholder:text-muted-foreground" />
            </div>
          </div>

          {filtered.length === 0
            ? <EmptyState icon={Calendar} title={t("analytics.noStandupMeetings")} description={search ? t("analytics.noStandupsMatchSearch") : undefined} />
            : (
              <div className="space-y-2">
                {filtered.map((m, i) => (
                  <motion.div key={m.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                    className="bg-card border border-border rounded-2xl overflow-hidden hover:border-primary/40 transition-all">
                    <button
                      onClick={() => setExpanded(expanded === m.id ? null : m.id)}
                      className="w-full flex items-center gap-4 p-4 text-left hover:bg-muted/20 transition-colors"
                    >
                      <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                        <CheckCircle className="w-4.5 h-4.5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground truncate">{m.title}</p>
                        <div className="flex items-center gap-3 mt-0.5">
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Calendar className="w-3 h-3" />{m.date}
                          </span>
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Clock className="w-3 h-3" />{m.duration}
                          </span>
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Users className="w-3 h-3" />{m.participants}
                          </span>
                        </div>
                      </div>
                      <Badge variant="secondary" className="text-[10px] shrink-0">{m.time}</Badge>
                    </button>

                    {expanded === m.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="border-t border-border px-4 pb-4 pt-3"
                      >
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Agenda Items</p>
                        <div className="space-y-1.5">
                          {m.items.map((item, j) => (
                            <div key={j} className="flex items-start gap-2">
                              <Circle className="w-3 h-3 text-primary shrink-0 mt-0.5" />
                              <span className="text-xs text-foreground">{item}</span>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </motion.div>
                ))}
              </div>
            )
          }
        </div>
      </div>
    </div>
  )
}
